import React, { useState, useEffect, useCallback } from 'react';
import { useAuthenticatedApi } from './api';
import './Alerts.css';

const Alerts = () => {
  const { authenticatedFetch, isAuthenticated } = useAuthenticatedApi();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alerts, setAlerts] = useState(null);
  const [formData, setFormData] = useState({
    contactMethod: 'off',
    opType: {
      Gigs: false,
      Full: false,
      Part: false,
      Temp: false,
      Creative: false,
      Access: false,
      Editor: false,
      Fellowship: false,
      Internship: false,
      Grant: false,
      Unpaid: false
    },
    topics: {
      // News
      Automotive: false,
      Books: false,
      Business: false,
      Climate: false,
      Education: false,
      Finance: false,
      Health: false,
      Identity: false,
      Investigative: false,
      Justice: false,
      Labor: false,
      Law: false,
      'Mental Health': false,
      News: false,
      Opinion: false,
      Politics: false,
      'Public Policy': false,
      Science: false,
      'Service Journalism': false,
      Sports: false,
      Technology: false,
      Wellness: false,
      // Arts and Culture
      Architecture: false,
      Art: false,
      Comedy: false,
      Culture: false,
      Design: false,
      Entertainment: false,
      'Film and TV': false,
      'Food and Drink': false,
      Gaming: false,
      Home: false,
      Lifestyle: false,
      Literature: false,
      Music: false,
      Style: false,
      Travel: false,
      // Content
      'Brand Writing': false,
      Copywriting: false,
      'E-commerce': false,
      Ghostwriting: false,
      SEO: false,
      // Creative
      Essays: false,
      Memoirs: false,
      'Non-Fiction': false,
      Novels: false,
      Poetry: false,
      'Short Stories': false
    },
    rateForm: {
      flexible: true
    }
  });
  const [pricePerPiece, setPricePerPiece] = useState(200);
  const [pricePerWord, setPricePerWord] = useState(1.00);
  const [email, setEmail] = useState('');

  // Define loadAlerts function first
  const loadAlerts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/alerts`);
      if (response.ok) {
        const data = await response.json();
        setAlerts(data);
        setEmail(data.profile?.contact_email || '');
        setPricePerPiece(data.piece_rate || 200);
        setPricePerWord(parseFloat(data.word_rate) || 1.00);
        
        // Parse existing form data
        if (data.method) {
          setFormData(prev => ({
            ...prev,
            contactMethod: data.method
          }));
        }
        
        if (data.op_type) {
          // op_type is already an object from the server
          setFormData(prev => ({
            ...prev,
            opType: { ...prev.opType, ...data.op_type }
          }));
        }
        
        if (data.topics) {
          // topics is already an object from the server
          setFormData(prev => ({
            ...prev,
            topics: { ...prev.topics, ...data.topics }
          }));
        }
        
            if (data.flexible_rate !== undefined) {
              setFormData(prev => ({
                ...prev,
                rateForm: { ...prev.rateForm, flexible: data.flexible_rate === 1 }
              }));
            }
            
      } else {
        console.error('Failed to load alerts:', response.status);
      }
    } catch (error) {
      console.error('Error loading alerts:', error);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isAuthenticated) {
      loadAlerts();
    }
  }, [isAuthenticated, loadAlerts]);

  const handleInputChange = (section, field, value) => {
    setFormData(prev => {
      if (section === 'contactMethod') {
        const newFormData = { ...prev, [section]: value };
        return newFormData;
      } else if (section === 'rateForm') {
        return { ...prev, [section]: { ...prev[section], [field]: value } };
      } else {
        return { ...prev, [section]: { ...prev[section], [field]: value } };
      }
    });
  };

  const handleSliderChange = (type, value) => {
    if (type === 'piece') {
      setPricePerPiece(value);
    } else if (type === 'word') {
      setPricePerWord(value);
    }
    // Auto-save on slider change
    setTimeout(() => {
      saveAlerts();
    }, 500);
  };

  const saveAlerts = useCallback(async () => {
    try {
      setSaving(true);
      
      const data = {
        method: formData.contactMethod,
        op_type: JSON.stringify(formData.opType),
        topics: JSON.stringify(formData.topics),
        piece_rate: pricePerPiece,
        word_rate: pricePerWord,
        flexible_rate: formData.rateForm.flexible ? 1 : 0
      };

          const response = await authenticatedFetch(`${process.env.REACT_APP_API_URL || '/api'}/alerts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
      } else {
        console.error('Error saving alerts');
      }
    } catch (error) {
      console.error('Error saving alerts:', error);
    } finally {
      setSaving(false);
    }
  }, [authenticatedFetch, formData, pricePerPiece, pricePerWord]);

  const handleFormChange = useCallback(() => {
    // Auto-save on form change
    setTimeout(() => {
      saveAlerts();
    }, 1000);
  }, [saveAlerts]);

  useEffect(() => {
    // Only auto-save if we have loaded initial data
    if (alerts !== null) {
      handleFormChange();
    }
  }, [formData, pricePerPiece, pricePerWord, alerts, handleFormChange]);

  if (loading) {
    return (
      <div className="alerts-container">
        <div className="alerts-loading-message">
          <p>Loading your alert preferences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="alerts-container">
      <h1 style={{ fontSize: '2.5rem', fontWeight: '700', color: '#1a1a1a', marginBottom: '1rem', textAlign: 'center' }}>Alerts</h1>
      
      <div className="alerts-intro">
        <p style={{ fontSize: '1.4rem', color: '#666', textAlign: 'center' }}>
          Study Hall can send you alerts based on your category preferences
        </p>
        <p style={{ fontSize: '1.1rem', color: '#666', textAlign: 'center' }}>
          When a job or writing opportunity is posted that matches the criteria you select below, you will be notified
        </p>
      </div>

      <form>
        <div className="alerts-form-section">
          <h2>ALERT METHOD</h2>
          
          <div className="radio-group">
            <label className="radio-option">
              <input
                type="radio"
                name="contactMethod"
                value="off"
                checked={formData.contactMethod === 'off'}
                onChange={(e) => handleInputChange('contactMethod', '', e.target.value)}
              />
              <span><b>OFF</b> - Do not send me alerts</span>
            </label>
            
            <label className="radio-option">
              <input
                type="radio"
                name="contactMethod"
                value="email"
                checked={formData.contactMethod === 'email'}
                onChange={(e) => handleInputChange('contactMethod', '', e.target.value)}
              />
              <span><b>EMAIL</b> - Send my alerts via email to {email}</span>
            </label>
          </div>
        </div>

        <div className="alerts-form-section">
          <h2>MINIMUM RATES</h2>
          <p><small>Some opportunities are listed by piece, others by the word. Please select both.</small></p>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
            <div>
              <h3>Per Piece: ${pricePerPiece}+</h3>
              <input
                type="range"
                min="0"
                max="500"
                step="50"
                value={pricePerPiece}
                onChange={(e) => handleSliderChange('piece', parseInt(e.target.value))}
                className="form-range"
              />
            </div>
            <div>
              <h3>Per Word: ${pricePerWord.toFixed(2)}+</h3>
              <input
                type="range"
                min="0"
                max="2"
                step="0.05"
                value={pricePerWord}
                onChange={(e) => handleSliderChange('word', parseFloat(e.target.value))}
                className="form-range"
              />
            </div>
          </div>
          
          <div style={{ marginTop: '1.5rem' }}>
            <label className="checkbox-option" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.rateForm.flexible}
                onChange={(e) => handleInputChange('rateForm', 'flexible', e.target.checked)}
              />
              <span>I am flexible. If an opportunity is a good match, please send it.</span>
            </label>
          </div>
        </div>

        <div className="alerts-form-section">
          <h2>OPPORTUNITY TYPE</h2>
          
          <div className="checkbox-grid">
            {Object.entries(formData.opType).map(([key, value]) => {
              // Map the keys to the correct display labels from Angular component
              const labelMap = {
                'Gigs': 'Gigs',
                'Full': 'Full Time',
                'Part': 'Part Time', 
                'Temp': 'Temporary',
                'Creative': 'Creative',
                'Access': 'Access',
                'Editor': 'Editor',
                'Fellowship': 'Fellowships',
                'Internship': 'Internships',
                'Grant': 'Grants',
                'Unpaid': 'Unpaid'
              };
              
              return (
                <label key={key} className="checkbox-option">
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => handleInputChange('opType', key, e.target.checked)}
                  />
                  <span className="fw-bold">{labelMap[key] || key}</span>
                  {key === 'Access' && (
                    <span 
                      className="tooltip-icon" 
                      data-tooltip="Access Opportunities connect journalists with publicists and marketers for interviews, press passes, and products for review."
                    >
                      &#9432;
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        </div>

        <div className="alerts-form-section">
          <h2>CATEGORIES</h2>
          
          <div className="category-group">
            <h4>News</h4>
            <div className="checkbox-grid">
              {['Automotive', 'Books', 'Business', 'Climate', 'Education', 'Finance', 'Health', 'Identity', 'Investigative', 'Justice', 'Labor', 'Law', 'Mental Health', 'News', 'Opinion', 'Politics', 'Public Policy', 'Science', 'Service Journalism', 'Sports', 'Technology', 'Wellness'].map(category => (
                <label key={category} className="checkbox-option">
                  <input
                    type="checkbox"
                    checked={formData.topics[category] || false}
                    onChange={(e) => handleInputChange('topics', category, e.target.checked)}
                  />
                  <span>{category}</span>
                </label>
              ))}
            </div>
          </div>
          
          <div className="category-group">
            <h4>Arts and Culture</h4>
            <div className="checkbox-grid">
              {['Architecture', 'Art', 'Comedy', 'Culture', 'Design', 'Entertainment', 'Film and TV', 'Food and Drink', 'Gaming', 'Home', 'Lifestyle', 'Literature', 'Music', 'Style', 'Travel'].map(category => (
                <label key={category} className="checkbox-option">
                  <input
                    type="checkbox"
                    checked={formData.topics[category] || false}
                    onChange={(e) => handleInputChange('topics', category, e.target.checked)}
                  />
                  <span>{category}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="category-group">
            <h4>Content</h4>
            <div className="checkbox-grid">
              {['Brand Writing', 'Copywriting', 'E-commerce', 'Ghostwriting', 'SEO'].map(category => (
                <label key={category} className="checkbox-option">
                  <input
                    type="checkbox"
                    checked={formData.topics[category] || false}
                    onChange={(e) => handleInputChange('topics', category, e.target.checked)}
                  />
                  <span>{category}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="category-group">
            <h4>Creative</h4>
            <div className="checkbox-grid">
              {['Essays', 'Memoirs', 'Non-Fiction', 'Novels', 'Poetry', 'Short Stories'].map(category => (
                <label key={category} className="checkbox-option">
                  <input
                    type="checkbox"
                    checked={formData.topics[category] || false}
                    onChange={(e) => handleInputChange('topics', category, e.target.checked)}
                  />
                  <span>{category}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </form>
      
      {saving && (
        <div className="saving-indicator">
          Saving preferences...
        </div>
      )}
    </div>
  );
};

export default Alerts;
